'use client';

import { createContext, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import type { Nullable } from '@libs/core';
import { DeliveryType, isLocaleCA, AUTOSHIP_DISCOUNT_BANNER_LOCAL_STORAGE_KEY } from '@libs/core';
import { useCart, useLocalStorage, usePageLayout, useAutoshipDecisions } from '../hooks';
import { hasFulfillmentMethodInCart } from '@libs/common-next/utils';
import type { ItemUI } from '@libs/common-node';

interface AutoshipPercentageProps {
    readonly bannerPercentage: string;
    readonly itemPercentages: Record<string, string>;
    readonly isBannerVisible: boolean;
    readonly hideBanner: () => void;
}

export const AutoshipPercentageContext = createContext<Nullable<AutoshipPercentageProps>>(undefined);

const initialPercentage = isLocaleCA() ? '15%' : '35%';

const activeSubscriptionPercentage = '5%';
const bannerPercentage = '5%';

export function AutoshipPercentageProvider({ children }: PropsWithChildren) {
    const [itemPercentages, setItemPercentages] = useState<Record<string, string>>({});
    const [isBannerVisible, setIsBannerVisible] = useState(false);
    const { flags } = usePageLayout();
    const { cart } = useCart();
    const autoshipDiscountUpdatesEnabled = Boolean(flags?.autoshipDiscountUpdatesEnabled);
    const [wasBannerShown, setWasBannerShown] = useLocalStorage<Record<string, boolean>>(
        AUTOSHIP_DISCOUNT_BANNER_LOCAL_STORAGE_KEY
    );
    const { isAutoshipCrawl, isAutoshipPreCrawl } = useAutoshipDecisions();

    const setStaticPercentageForAllItems = (itemIds: string[], percentage: string) => {
        setItemPercentages(prevState => ({
            ...prevState,
            ...Object.fromEntries(itemIds.map(id => [id, percentage])),
        }));
    };

    const getDynamicPercentages = (items: ItemUI[]) =>
        Object.fromEntries(items.map(item => [item.id, item.autoshipDiscount ?? initialPercentage]));

    useEffect(() => {
        if (!cart) {
            return;
        }

        const items = cart.fulfillmentGroups.flatMap(({ items }) => items.map(item => item));
        const itemIds = items.map(item => item.id);

        if (autoshipDiscountUpdatesEnabled) {
            const hasAutoshipInCart = hasFulfillmentMethodInCart(cart, DeliveryType.AUTOSHIP);

            if (!hasAutoshipInCart) {
                setIsBannerVisible(false);
            }

            const hasAutoshipOrders = cart?.hasAutoshipOrders;

            if (hasAutoshipInCart && hasAutoshipOrders) {
                setStaticPercentageForAllItems(itemIds, activeSubscriptionPercentage);

                if (cart?.id && !wasBannerShown?.[cart.id]) {
                    setIsBannerVisible(true);
                    setWasBannerShown({
                        ...wasBannerShown,
                        [cart.id]: true,
                    });
                }

                return;
            }

            if (hasAutoshipOrders) {
                setStaticPercentageForAllItems(itemIds, activeSubscriptionPercentage);
                return;
            }

            if (isAutoshipCrawl || isAutoshipPreCrawl) {
                setItemPercentages(prevState => ({
                    ...prevState,
                    ...getDynamicPercentages(items),
                }));
            } else {
                setStaticPercentageForAllItems(itemIds, initialPercentage);
            }
        } else {
            if (!cart?.hasAutoshipOrders) {
                setStaticPercentageForAllItems(itemIds, initialPercentage);
            }
        }
    }, [autoshipDiscountUpdatesEnabled, isAutoshipCrawl, isAutoshipPreCrawl, cart]);

    const hideBanner = useCallback(() => {
        setIsBannerVisible(false);
    }, []);

    const contextValue = useMemo(
        () => ({
            bannerPercentage,
            itemPercentages,
            isBannerVisible,
            hideBanner,
        }),
        [itemPercentages, isBannerVisible, hideBanner]
    );

    return <AutoshipPercentageContext.Provider value={contextValue}>{children}</AutoshipPercentageContext.Provider>;
}
